import type { ServerError, ServerParseError } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { datadogLogs } from '@datadog/browser-logs';
import * as Sentry from '@sentry/react';
import type { GraphQLFormattedError } from 'graphql';

import {
  ignoreCloudRouterErrors,
  ignorePermissionsErrors,
} from 'src/lib/apollo/catchErrors';

const ignoredHttpStatusCodes = [401];

const isTransportError = (
  networkError: Error | ServerError | ServerParseError,
): networkError is ServerError | ServerParseError =>
  Object.prototype.hasOwnProperty.call(networkError, 'statusCode');

export interface SentryReportingContext {
  /**
   * Callback functions which should return true if the error should be ignored,
   * otherwise should return false.  Filtered errors will still be forwarded
   * through the link chain, just not reported to Sentry.
   */
  ignoreSentryReporting?: ((error: GraphQLFormattedError) => boolean)[];
}

export const sentryErrorReporting = onError(
  ({ graphQLErrors, networkError, operation }) => {
    // attempt to make this somewhat more type-safe
    const { ignoreSentryReporting }: SentryReportingContext =
      operation.getContext();

    graphQLErrors
      ?.filter((e) => {
        try {
          const wasIgnored = [
            ignorePermissionsErrors,
            ignoreCloudRouterErrors,
            ...(ignoreSentryReporting ?? []),
          ].some((ignoreError) => ignoreError(e));
          return !wasIgnored;
        } catch (error) {
          console.warn(error); // eslint-disable-line no-console
        }
        // if not ignored, send to Sentry and Datadog
        return true;
      })
      .forEach((e) => {
        // Send to Sentry (existing behavior)
        Sentry.captureMessage(e.message, {
          level: 'error',
          tags: {
            component: 'graphql',
            operationName: operation.operationName,
          },
        });

        // Also send to Datadog Logs
        // This is a short term experiment - the hypothesis is that all errors
        // in Datadog will be better (than Sentry) because we monitor
        // everything else in datadog, so dashboards, alerting, etc will be
        // easier in Datadog.
        datadogLogs.logger.error('GraphQL Error', {
          message: e.message,
          operationName: operation.operationName,
          path: e.path,
          extensions: e.extensions,
          errorType: 'graphql',
        });
      });

    if (
      networkError &&
      isTransportError(networkError) &&
      !ignoredHttpStatusCodes.includes(networkError.statusCode)
    ) {
      // Send to Sentry (existing behavior)
      Sentry.captureMessage(networkError.message, {
        // use warning level here as it most likely is an auth or transport issue
        level: 'warning',
        tags: {
          component: 'link.network',
          statusCode: `${networkError.statusCode}`,
          operationName: operation.operationName,
        },
      });

      // Also send to Datadog Logs
      // This is a short term experiment - the hypothesis is that all errors
      // in Datadog will be better (than Sentry) because we monitor
      // everything else in datadog, so dashboards, alerting, etc will be
      // easier in Datadog.
      datadogLogs.logger.warn('Network Error', {
        message: networkError.message,
        statusCode: networkError.statusCode,
        operationName: operation.operationName,
        errorType: 'network',
      });
    }
  },
);
